import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  ChevronDownIcon,
  XIcon, 
  SearchIcon
} from 'lucide-react';
import { Invoice, Subscription, Customer, subscriptionServices, customerServices } from '../lib/supabase';
import { fixedInvoiceServices } from '../lib/fixed-invoice-payment-services';
import { formatCurrency, formatInvoiceNumber } from '../lib/formatters';

const Invoices = () => {
  const [invoices, setInvoices] = useState<(Invoice & { customerName?: string, subscriptionPlan?: string })[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState<Partial<Invoice> | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Fetch invoices and related data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Get invoices with fixed service
        const { data: invoicesData, error: invoicesError } = await fixedInvoiceServices.getAll();
        if (invoicesError) throw new Error('Failed to fetch invoices');
        
        // Get subscriptions
        const { data: subscriptionsData, error: subscriptionsError } = await subscriptionServices.getAll();
        if (subscriptionsError) throw new Error('Failed to fetch subscriptions');
        setSubscriptions(subscriptionsData || []);
        
        // Get customers
        const { data: customersData, error: customersError } = await customerServices.getAll();
        if (customersError) throw new Error('Failed to fetch customers');
        setCustomers(customersData || []);
        
        // Enrich invoice data with customer and subscription info
        const enrichedInvoices = (invoicesData || []).map(invoice => {
          const subscription = subscriptionsData?.find(s => s.id === invoice.subscription_id);
          const customer = customersData?.find(c => c.id === subscription?.customer_id);
          
          return {
            ...invoice,
            customerName: customer?.name || 'Unknown Customer',
            subscriptionPlan: customer?.plan || 'Unknown Plan'
          };
        });
        
        setInvoices(enrichedInvoices);
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Open modal to add/edit invoice
  const handleOpenModal = (invoice: Invoice | null = null) => {
    if (invoice) {
      setCurrentInvoice(invoice);
    } else {
      // Default values for new invoice
      setCurrentInvoice({
        subscription_id: '',
        amount: 0,
        status: 'Unpaid',
        due_date: new Date().toISOString().split('T')[0],
      });
    }
    setShowModal(true);
  };
  
  // Close modal and reset form
  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentInvoice(null);
  };
  
  // Handle invoice form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentInvoice || !currentInvoice.subscription_id || !currentInvoice.amount || !currentInvoice.due_date) {
      setError('Please fill all required fields');
      return;
    }
    
    try {
      let result: { data: Invoice[] | null; error: Error | null };
      
      if (currentInvoice.id) {
        // Update existing invoice with fixed service
        result = await fixedInvoiceServices.update(currentInvoice.id, currentInvoice);
        
        // Update local state
        setInvoices(prevInvoices => 
          prevInvoices.map(invoice => 
            invoice.id === currentInvoice.id 
              ? { ...invoice, ...currentInvoice } 
              : invoice
          )
        );
      } else {
        // Create new invoice
        const newInvoice = {
          ...currentInvoice,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as Invoice;
        
        result = await fixedInvoiceServices.create(newInvoice);
        
        // Update local state with enriched invoice data
        if (result.data && result.data.length > 0) {
          const createdInvoice = result.data[0];
          const subscription = subscriptions.find(s => s.id === createdInvoice.subscription_id);
          const customer = customers.find(c => c.id === subscription?.customer_id);
          
          const enrichedInvoice = {
            ...createdInvoice,
            customerName: customer?.name || 'Unknown Customer',
            subscriptionPlan: customer?.plan || 'Unknown Plan'
          };
          
          setInvoices(prevInvoices => [enrichedInvoice, ...prevInvoices]);
        }
      }
      
      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };
  
  // Handle invoice cancellation
  const handleCancel = async (id: string) => {
    if (!window.confirm('Are you sure you want to cancel this invoice?')) return;
    
    try {
      const result = await fixedInvoiceServices.update(id, { 
        status: 'Cancelled',
        updated_at: new Date().toISOString()
      });
      if (result.error) throw result.error;

      setInvoices(prevInvoices => prevInvoices.map(inv => 
        inv.id === id ? { ...inv, status: 'Cancelled' } : inv
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel invoice');
    }
  };

  // Filter invoices based on search query
  const filteredInvoices = invoices.filter(invoice => 
    invoice.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    invoice.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
    invoice.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Get customer name from subscription id
  const getCustomerNameFromSubscription = (subscriptionId: string) => {
    const subscription = subscriptions.find(s => s.id === subscriptionId);
    if (!subscription) return 'Unknown';
    
    const customer = customers.find(c => c.id === subscription.customer_id);
    return customer?.name || 'Unknown';
  };
  
  // Get status color class
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Partially Paid':
        return 'bg-yellow-100 text-yellow-800';
      case 'Unpaid':
        return 'bg-orange-100 text-orange-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'Cancelled':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format date to readable format
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <PlusIcon size={16} className="mr-1" /> Create Invoice
        </button>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-800">
            <XIcon size={16} />
          </button>
        </div>
      )}
      
      <div className="mb-4 relative">
        <input
          type="text"
          placeholder="Search invoices..."
          className="px-10 py-2 border rounded w-full"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="absolute left-3 top-3 text-gray-400">
          <SearchIcon size={18} />
        </div>
      </div>
      
      {isLoading ? (
        <div className="text-center py-10">Loading invoices data...</div>
      ) : filteredInvoices.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          {searchQuery ? 'No invoices match your search.' : 'No invoices found.'}
        </div>
      ) : (
        <div className="overflow-x-auto shadow rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Paid Date
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <Link 
                      to={`/invoices/${invoice.id}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {formatInvoiceNumber(invoice)}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invoice.customerName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invoice.subscriptionPlan}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {formatCurrency(invoice.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invoice.due_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invoice.paid_at ? formatDate(invoice.paid_at) : 'Unpaid'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleOpenModal(invoice as Invoice)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                      title="Edit Invoice"
                    >
                      <PencilIcon size={16} />
                    </button>
                    <button
                      onClick={() => handleCancel(invoice.id!)}
                      className="text-red-600 hover:text-red-900"
                      title="Cancel Invoice"
                    >
                      <TrashIcon size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Form Modal for Add/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
            <button 
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <XIcon size={20} />
            </button>
            
            <h2 className="text-xl font-semibold mb-4">
              {currentInvoice?.id ? 'Edit Invoice' : 'Create New Invoice'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subscription*
                </label>
                <div className="relative">
                  <select
                    value={currentInvoice?.subscription_id || ''}
                    onChange={(e) => setCurrentInvoice({
                      ...currentInvoice,
                      subscription_id: e.target.value
                    })}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    required
                  >
                    <option value="">Select a subscription</option>
                    {subscriptions.map(subscription => (
                      <option key={subscription.id} value={subscription.id}>
                        {getCustomerNameFromSubscription(subscription.id!)} - {subscription.status}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <ChevronDownIcon size={16} />
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount*
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={currentInvoice?.amount || ''}
                  onChange={(e) => setCurrentInvoice({
                    ...currentInvoice,
                    amount: parseFloat(e.target.value)
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={currentInvoice?.status || 'Unpaid'}
                  onChange={(e) => setCurrentInvoice({
                    ...currentInvoice,
                    status: e.target.value as Invoice['status']
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="Unpaid">Unpaid</option>
                  <option value="Paid">Paid</option>
                  <option value="Partially Paid">Partially Paid</option>
                  <option value="Overdue">Overdue</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date*
                </label>
                <input
                  type="date"
                  value={currentInvoice?.due_date ? currentInvoice.due_date.toString().substring(0, 10) : ''}
                  onChange={(e) => setCurrentInvoice({
                    ...currentInvoice,
                    due_date: e.target.value
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                />
              </div>
              
              {currentInvoice?.status === 'Paid' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    value={currentInvoice?.paid_at ? currentInvoice.paid_at.toString().substring(0, 10) : ''}
                    onChange={(e) => setCurrentInvoice({
                      ...currentInvoice,
                      paid_at: e.target.value
                    })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              )}
              
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {currentInvoice?.id ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
